import React from "react";
import { LocalKeys, useTranslation } from "utilities/i18n";
import {
  Rate,
  TeamsWebhookActionConfiguration
} from "generated/graphql.schema";
import { AutoSave, Form, Formik, Select, ToggleSwitch } from "collections";
import { NOTIFICATION_ACTION_TYPES } from "Constants";
import { SubForms } from "./SubForms";
import * as yup from "yup";
import { useUpdateTeamsActionConfigurationMutation } from "generated/graphql";
import { ChangeSaveNotification } from "../../ServiceNow/Edit/ChangeSaveNotification";
import { getDividerIndex } from "../../Helpers";

const TeamsWebhookActionConfigurationSchema = yup.object({
  rate: yup
    .string()
    .oneOf(Object.values(Rate))
    .required(),
  enabled: yup.boolean().required(),
  allowedRates: yup.array().of(yup.string())
});

const TeamsNotificationTypeForm: React.FC<{
  webHooksId: string;
  ac: TeamsWebhookActionConfiguration;
}> = ({ ac: { actionType, ...rest } }) => {
  const { t } = useTranslation();
  const [, update] = useUpdateTeamsActionConfigurationMutation();

  return (
    <Formik
      name="TeamsWebhookActionConfiguration"
      validationSchema={TeamsWebhookActionConfigurationSchema}
      initialValues={rest}
      onSubmit={async values => {
        await update({
          input: {
            enabled: values.enabled,
            rate: values.rate as Rate, //Validated by TeamsWebhookActionConfigurationSchema
            actionId: rest.id
          }
        });
      }}
    >
      {({ field, isSubmitting, values }) => {
        const dividerIndex = getDividerIndex(values.allowedRates);
        return (
          <Form className="form grid">
            <ChangeSaveNotification />
            <AutoSave debounceMs={200} />
            <div className="form-group">
              <label
                className="col-3 text-right"
                htmlFor={"notification-types-input"}
              >
                {t(
                  NOTIFICATION_ACTION_TYPES[actionType.type] ?? actionType.type
                )}
                :
              </label>
              <div className="col-1">
                <ToggleSwitch
                  {...field.enabled}
                  disabled={isSubmitting}
                  id={`${rest.id}`}
                />
              </div>
              {values.enabled && (
                <div className="col-3 col-xl-2">
                  <Select
                    placeholder="Frequency"
                    {...field.rate}
                    dividerIndices={dividerIndex ? [dividerIndex] : []}
                    options={values.allowedRates.map(ar => {
                      return {
                        value: ar,
                        text: t(ar as LocalKeys)
                      };
                    })}
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export const TeamsNotificationTypeForms: React.FC<SubForms> = ({
  webHooksId,
  ac
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="mt-15 ph-2 form">
        <label>{t("NOTIFICATION_TYPES")}</label>
      </div>
      {ac.actionConfigurations.map(ac => (
        <TeamsNotificationTypeForm
          webHooksId={webHooksId}
          ac={ac}
          key={ac.id}
        />
      ))}
    </>
  );
};
